import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAttributes, useValue } from '@aytm/app/insights/dashboard/components/hooks'
import PropTypes from 'prop-types'

import usePool from '@duckness/use-pool'
import InsightsDashboardPool from '@aytm/app/insights/dashboard/duckness/InsightsDashboardPool'

import { chartTypes, interpolate } from '@aytm/app/insights/dashboard/lib/charts'
import { defaultColors } from '@aytm/app/insights/dashboard/lib/colors'
import { isRowAveragesUid } from '@aytm/app/insights/dashboard/lib/config'
import {
  renderChart,
  updateChart,
  seriesName,
  showCollapsedSections,
  showAttributeMetrics,
  showUniqueAttributes
} from '../config'

import './BlockChart.css'
import { HamburgerIcon } from '../HamburgerIcon'
import { safelyExecuteSync } from '@aytm/app/insights/dashboard/lib/safety'

import { stateSelector, shouldUpdate } from './selectors'

const CLASS = 'insights-portal-block-chart'

const BlockChart = props => {
  const chartId = `${CLASS}-${props.waveIndex}`

  const onChartTypeChange = props.onChartTypeChange || null

  const containerRef = useRef(null)

  const subjectType = props.subjectType || ''

  /** Get attribute captions */
  const { attributes, getCaptions } = useAttributes()

  const [fullScreen, setFullScreen] = useState(false)

  const waveIndex = props.waveIndex || 0

  const {
    isSharedView,
    subjects,
    splitOptions,
    waveOptions,
    data,
    blockChartType,
    blockChartScale,
    sortByAttribute,
    sortByMethod
  } = usePool(InsightsDashboardPool, stateSelector, shouldUpdate)

  /** getColumnData function */
  const { getColumnItem, getCellData } = useValue()

  /** Chart type */
  const chartType = useMemo(() => chartTypes.find(v => blockChartType === v.id) || chartTypes[0], [blockChartType])

  const subjectUids = useMemo(
    () =>
      splitOptions.reduce((acc, splitOption) => {
        const subject = subjects
          ? Object.values(subjects).find(v => v.wave?.index === waveIndex && v.split?.id === splitOption.id)
          : null
        if (subject && data[subject.uid]) {
          acc.push({ uid: subject.uid, key: `${splitOption.id}` })
        } else {
          acc.push({ uid: null, key: `${splitOption.id}` })
        }
        return acc
      }, []),
    [data, splitOptions, subjects, waveIndex]
  )

  const title = useMemo(() => {
    const waveOption = waveOptions.find(v => v.index === waveIndex)
    return waveOption ? waveOption.text : ''
  }, [waveIndex, waveOptions])

  const chart = useRef()

  const series = useMemo(() => {
    // Create captions for attributes in all sections
    const captions = getCaptions({
      showSections: false,
      showMetrics: showAttributeMetrics,
      showMetadata: false,
      showCollapsed: showCollapsedSections,
      showUnique: showUniqueAttributes
    })
    // Add data series
    const result =
      subjectUids && data && captions?.length
        ? subjectUids
            .filter(v => !isRowAveragesUid(v.uid))
            .reduce((acc, cur) => {
              for (const [
                attributeIndex,
                { section, attribute, metrics, defaultMetrics, disabled }
              ] of captions.entries()) {
                const selected = attribute === sortByAttribute && (!metrics?.id || metrics?.id === sortByMethod)
                const ser = acc.find(
                  v =>
                    section === v.custom.section &&
                    attribute === v.custom.attribute &&
                    (!metrics?.id || metrics?.id === v.custom.metrics?.id)
                ) || {
                  name: seriesName(attributes, attribute, metrics),
                  custom: {
                    section,
                    attribute,
                    metrics
                  },
                  data: [],
                  lineWidth: selected ? 3 : 1,
                  color: defaultColors[attributeIndex % defaultColors.length],
                  shadow: selected
                    ? {
                        color: defaultColors[attributeIndex % defaultColors.length],
                        width: 7,
                        offsetX: 0,
                        offsetY: 0,
                        opacity: 0.25
                      }
                    : false,
                  marker: { symbol: 'circle', radius: 7 },
                  zIndex: selected ? 1 : 0,
                  visible: disabled.includes(metrics?.id) ? false : true
                }
                if (!ser.data.length) acc.push(ser)
                const item = getColumnItem(cur.uid)
                const { value } = getCellData(item, attribute, metrics, defaultMetrics)
                if (null === value || undefined === value) {
                  ser.data.push([cur.key, null])
                } else {
                  ser.data.push([cur.key, Math.round(100 * value || 0) / 100])
                }
              }
              return acc
            }, [])
        : []
    // Interpolate data if needed
    return ['line', 'area'].includes(chartType.type) ? result.map(v => interpolate(v)) : result
  }, [
    attributes,
    chartType.type,
    data,
    getCaptions,
    getCellData,
    getColumnItem,
    sortByAttribute,
    sortByMethod,
    subjectUids
  ])

  useEffect(() => {
    if (!fullScreen) {
      chart.current = chart.current
        ? safelyExecuteSync(
            () =>
              updateChart({
                chart: chart.current,
                series,
                scale: blockChartScale,
                title,
                chartType,
                marginTop: 64,
                setChartType: event => onChartTypeChange({ blockChartType: event.id }),
                setFullScreen: setFullScreen,
                isSharedView
              }),
            chart.current || null
          )
        : safelyExecuteSync(
            () =>
              renderChart({
                id: chartId,
                series,
                scale: blockChartScale,
                title,
                chartType,
                marginTop: 64,
                setChartType: event => onChartTypeChange({ blockChartType: event.id }),
                setFullScreen: setFullScreen,
                isSharedView
              }),
            chart.current || null
          )
    }
  }, [
    props.timeout,
    series,
    blockChartScale,
    chartId,
    title,
    chartType,
    onChartTypeChange,
    fullScreen,
    isSharedView,
    subjectType
  ])

  useEffect(() => {
    const node = containerRef.current
    if (!node) return
    const observer = new ResizeObserver(() => chart.current?.reflow())
    observer.observe(node)
    return () => observer.disconnect()
  }, [series?.length > 0])

  const className = props.className || CLASS

  if (!series?.length) return null

  return (
    <div className={className} style={props.style} ref={containerRef}>
      <div className="export-button-container">
        <button className="export-button" type="button">
          <HamburgerIcon />
        </button>
      </div>
      <div style={{ width: '100%' }} id={chartId} />
    </div>
  )
}

BlockChart.propTypes = {
  wait: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  waveIndex: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.object),
  timeout: PropTypes.number,
  scrollLeft: PropTypes.number,
  blockWidth: PropTypes.number,
  subjectType: PropTypes.string,
  onChartTypeChange: PropTypes.func
}

BlockChart.defaultProps = {
  wait: false,
  waveIndex: 0,
  items: [],
  timeout: 700,
  scrollLeft: 0
}

const EmptyChart = props => {
  const className = props.className || CLASS
  return <div className={className} style={props.style}></div>
}

EmptyChart.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object
}

const propsAreEqual = (prev, cur) =>
  !cur.wait &&
  prev.waveIndex === cur.waveIndex &&
  prev.className === cur.className &&
  prev.style === cur.style &&
  prev.subjectType === cur.subjectType &&
  prev.timeout === cur.timeout &&
  prev.onChartTypeChange === cur.onChartTypeChange

const MemoBlockChart = React.memo(BlockChart, propsAreEqual)

MemoBlockChart.Empty = EmptyChart

export default MemoBlockChart
